import { useFormik } from "formik";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import {
  useGetOrderTypeQuery,
  useLazyGetGliListsRecheckQuery,
  useSubmitGLIRecheckDataMutation,
} from "../../../../store/queries/Printing";
import { updateConfig } from "../../../../store/slices/Printing/printingSlice";

const useGLIPrintRecheck = () => {
  const dispatch = useDispatch();
  const state = useSelector((s) => s.printing);
  const slug = sessionStorage?.getItem("slug");

  const [selectedValId, setSelectedValId] = useState([]);
  const [gliItems, setGliItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [gliEntries, setGliEntries] = useState([
    { id: 1, gli_number: null, status: [], report_id: null, _id: null },
  ]);

  const [triggerGetGliLists] = useLazyGetGliListsRecheckQuery();
  const [submitGLIRecheckData] = useSubmitGLIRecheckDataMutation();
  const { data: ItemType } = useGetOrderTypeQuery();

  const formattedItemTypeData = ItemType?.data?.map(({ _id, name }) => ({
    id: _id,
    name,
  }));

  const loadGliOptions = async (search, loadedOptions, { page }) => {
    try {
      const result = await triggerGetGliLists({
        item_type: selectedValId,
        process_slug: slug,
        search,
        page: page || 1,
        page_size: 20,
      }).unwrap();

      const gliData = result?.data?.data || [];
      const usedGliIds = gliEntries
        .map((entry) => entry.gli_number?._id)
        .filter(Boolean);
      const filteredGliData = gliData.filter(
        (item) => !usedGliIds.includes(item._id)
      );

      setGliItems((prev) => {
        const existingIds = new Set(prev.map((item) => item._id));
        const newItems = filteredGliData.filter(
          (item) => !existingIds.has(item._id)
        );
        return [...prev, ...newItems];
      });

      return {
        options: filteredGliData,
        hasMore: gliData.length === 20,
        additional: { page: (page || 1) + 1 },
      };
    } catch (error) {
      console.error("Failed to fetch recheck GLI options:", error);
      return {
        options: [],
        hasMore: false,
        additional: { page: page || 1 },
      };
    }
  };

  const validationSchema = Yup.object({
    item_type: Yup.object().required("Select Item Type"),
    certificate_type: Yup.object().required("Select Certificate Type"),
    gli_data: Yup.array()
      .of(
        Yup.object({
          _id: Yup.string().nullable(),
          gli_number: Yup.mixed().required("GLI is required"),
          status: Yup.array()
            .of(Yup.number())
            .min(1, "At least one status is required")
            .required("Status is required"),
          report_id: Yup.mixed().required("Report Type is required"),
        })
      )
      .min(1, "At least one GLI entry is required"),
    side: Yup.object().test(
      "conditional-validation",
      "Select Side of Certificate",
      function (value) {
        const certificateTypeId = this.parent.certificate_type?._id;
        if (certificateTypeId === 1 || certificateTypeId === 3) {
          return value !== undefined && value !== null && value !== "";
        }
        return true;
      }
    ),
    card_size: Yup.object().test(
      "conditional-validation",
      "Select Card Size of Certificate",
      function (value) {
        const certificateTypeId = this.parent.certificate_type?._id;
        if (certificateTypeId === 1) {
          return value !== undefined && value !== null && value !== "";
        }
        return true;
      }
    ),
  });

  const initialValues = {
    certificate_type: "",
    side: "",
    card_size: { name: "Normal (7.8)", id: 1 },
    remarks: "",
    certificate_format: "",
    gli_data: [{ gli_number: null, status: [], report_id: null, _id: null }],
    item_type: "",
    gli: [],
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    validate: (values) => {
      const errors = {};
      if (
        (values?.certificate_type?._id === 1 && values?.side?.id === 1) ||
        values?.certificate_type?._id === 2
      ) {
        if (!values?.certificate_format) {
          errors.certificate_format = "Select one";
        }
      }
      return errors;
    },
    onSubmit: (values) => {
      // Guard against double submits while the GLI data request is in flight.
      if (isLoading) {
        return;
      }
      setIsLoading(true);
      const Ids = values?.gli_data?.map((val) => val?._id);

      const transformedGliData =
        values.gli_data?.map((entry) => ({
          gli_number: entry.gli_number,
          receipt_id: entry._id,
          report_id: entry.report_id,
          status: entry.status,
        })) || [];

      const submitGliData = transformedGliData.map((entry) => ({
        receipt_id: entry.receipt_id,
        status: entry.status,
      }));

      const body = {
        gli_data: submitGliData,
        search: state.search ?? "",
        page: 1,
        per_page: state.currentRecheckPageSize ?? 60,
      };

      submitGLIRecheckData(body)
        .unwrap()
        .then((res) => {
          if (res?.status_code === 200) {
            dispatch(
              updateConfig((s) => {
                s.transformedGliDataOrder = transformedGliData;
                s.currentRecheckPage = 1;
                s.selectedId = Ids;
                s.showTable = true;
                s.isMultiGliPrint = false;
                s.is_multi_print = true;
                s.isMultiPrint = true;
                s.gliform = {
                  certificate_type: values.certificate_type,
                  side: values.side,
                  card_size: values.card_size,
                  remarks: values.remarks,
                  certificate_format: values.certificate_format,
                  gli_data: values.gli_data,
                  item_type: values.item_type,
                  gli: values.gli,
                };
                s.clear = true;
                s.multipleTableData = res.data;
                s.multipleTableAllData = res.data?.data ?? [];
              })
            );
          }
        })
        .catch((err) => {
          console.error("Recheck multi GLI submit failed:", err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
  });

  const handleItemChange = (val) => {
    setSelectedValId([val?.id]);
    formik.setValues({
      ...initialValues,
      item_type: val,
    });
    formik.setErrors({});
    formik.setTouched({});
    formik.setFieldValue("gli", []);
    setGliEntries([
      { id: 1, gli_number: null, status: [], report_id: null, _id: null },
    ]);
  };

  const handleCloseModal = () => {
    dispatch(
      updateConfig((s) => {
        s.isMultiGliPrint = false;
      })
    );
    setIsLoading(false);
  };

  const getFieldError = (fieldName) => {
    if (formik.touched[fieldName] && formik.errors[fieldName]) {
      return formik.errors[fieldName];
    }
    return "";
  };

  const setCardSize = (value) => {
    formik?.setFieldValue("card_size", value || null);
    dispatch(
      updateConfig((s) => {
        s.card_size = value?.id;
      })
    );
  };

  return {
    formik,
    isLoading,
    handleCloseModal,
    getFieldError,
    setCardSize,
    gliItems,
    uniqueItemTypes: formattedItemTypeData ?? [],
    handleItemChange,
    gliEntries,
    setGliEntries,
    loadGliOptions,
    selectedValId,
  };
};

export default useGLIPrintRecheck;
